import React from 'react';
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHead } from '@/components/ui/table';
import { Invoice } from '@/services/types/invoice.types';
import { Budget } from '@/services/types/budgets.types';
import { InvoiceCategory } from '@/services/types/enums/invoice.enum';
import { format, formatDistanceToNow } from 'date-fns';
import { Loader2 } from 'lucide-react';

interface InvoicesTableProps {
  invoices: Invoice[];
  setSelectedInvoice: (invoice: Invoice & { budget?: Budget }) => void;
  isDraft?: boolean;
  onInvoiceSelect?: (invoice: Invoice & { budget?: Budget }) => void;
  loading?: boolean;
}

const InvoicesTable = ({
  invoices = [],
  setSelectedInvoice,
  isDraft = false,
  onInvoiceSelect,
  loading = false
}: InvoicesTableProps) => {
  const handleRowClick = (invoice: Invoice) => {
    const formattedInvoice = { ...invoice, budget: invoice.budget || undefined };

    if (onInvoiceSelect) {
      onInvoiceSelect(formattedInvoice);
      return;
    }

    setSelectedInvoice(formattedInvoice);
  };

  // Calculate the number of columns dynamically
  const totalColumns = isDraft ? 2 : 6;

  return (
    <>
      <div className="flex-1 min-w-0 hidden md:block">
        <Table className="w-full min-w-0 hidden md:table max-w-full">
          <TableHeader>
            <TableRow>
              {!isDraft && <TableHead className="w-[100px] text-secondary">Date</TableHead>}
              <TableHead className="text-secondary">Invoice Name</TableHead>
              {!isDraft && (
                <>
                  <TableHead className="text-secondary">Budget</TableHead>
                  <TableHead className="text-secondary">Type</TableHead>
                  <TableHead className="text-secondary">Amount</TableHead>
                </>
              )}
              <TableHead className="text-secondary">Updated At</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={totalColumns} className="py-10 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-secondary" />
                    <p className="text-sm font-medium text-secondary">Loading invoices...</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalColumns} className="py-10 text-center">
                  <p className="text-sm font-medium text-secondary">
                    {isDraft ? 'No draft invoices found' : 'No invoices found'}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => {
                return (
                  <TableRow
                    key={invoice._id}
                    className="border-none hover:bg-[#DBD6FF66] rounded-[8px] cursor-pointer"
                    onClick={() => handleRowClick(invoice)}
                  >
                    {!isDraft && (
                      <TableCell className="text-sm rounded-tl-[8px] rounded-bl-[8px]">
                        {new Date(invoice?.createdAt || '').toLocaleDateString()}
                      </TableCell>
                    )}
                    <TableCell className={`text-sm ${isDraft ? 'rounded-tl-[8px] rounded-bl-[8px]' : ''}`}>
                      {invoice.name}
                    </TableCell>
                    {!isDraft && (
                      <>
                        <TableCell className="text-sm">{invoice.budget?.name}</TableCell>
                        <TableCell className="text-sm">
                          {invoice.invoice_category === InvoiceCategory.REVENUE ? (
                            <span className="inline-block bg-[#48C80C99] py-[2px] px-[6px] text-white text-xs font-medium rounded-full">
                              Revenue
                            </span>
                          ) : (
                            <span className="inline-block bg-[#E8507E99] py-[2px] px-[6px] text-white text-xs font-medium rounded-full">
                              Expense
                            </span>
                          )}
                        </TableCell>
                        <TableCell
                          className="text-sm rounded-tr-[8px] rounded-br-[8px]"
                          style={{
                            color: invoice.invoice_category === InvoiceCategory.REVENUE ? '#48C80C' : '#E8507E'
                          }}
                        >
                          {invoice.invoice_category === InvoiceCategory.REVENUE ? '+' : '-'} ${invoice.payment_amount}
                        </TableCell>
                      </>
                    )}
                    <TableCell className={`text-sm ${isDraft ? 'rounded-tr-[8px] rounded-br-[8px]' : ''}`}>
                      Last edited: {formatDistanceToNow(new Date(invoice.updatedAt || ''), { addSuffix: true })}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex-1 flex flex-col gap-2 md:hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10">
            <Loader2 className="w-5 h-5 animate-spin text-secondary" />
            <p className="text-sm font-medium text-secondary">Loading invoices...</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <p className="text-sm font-medium text-secondary">
              {isDraft ? 'No draft invoices found' : 'No invoices found'}
            </p>
          </div>
        ) : (
          invoices.map((invoice) => {
            return (
              <button
                onClick={() => handleRowClick(invoice)}
                type="button"
                className="w-full flex flex-col gap-1 border-b pb-2.5 last:border-b-0"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-left">
                    {invoice.invoice_category === InvoiceCategory.REVENUE ? 'Revenue' : 'Expense'}
                  </span>

                  <span className="text-xs text-secondary font-medium text-left">
                    {invoice.createdAt ? format(new Date(invoice.createdAt), 'MMM d, yyyy') : '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">{invoice.name}</span>
                  <span
                    className="text-sm font-semibold"
                    style={{
                      color: invoice.invoice_category === InvoiceCategory.REVENUE ? '#48C80C' : '#E8507E'
                    }}
                  >
                    {invoice.invoice_category === InvoiceCategory.REVENUE ? '+' : '-'} ${invoice.payment_amount}
                  </span>
                </div>
              </button>
            );
          })
        )}
      </div>
    </>
  );
};

export default InvoicesTable;
